/**
 * Which panels the settings sidebar lists, beyond the role and purchase gates of
 * `useSettingsNavigation`. See docs/settings-sidebar-panels.md.
 *
 * `Customer.configuration.portalPanels` (set in Mongo, there is no UI for it) narrows the sidebar
 * to the configured panels plus `ALWAYS_VISIBLE_PANELS`. It hides, it does not protect: a panel
 * left out still opens through `?panel=…`.
 */

/** Kept whatever `portalPanels` says — each one still behind its own role check. */
export const ALWAYS_VISIBLE_PANELS = [
    // Hängt an der Freigabe des Marktplatz-Eintrags (useSettingsNavigation), nicht an portalPanels.
    "guidelines",
    "companyProfile",
    "teamManagement",
    "agent",
    "fileManagement",
    "orders",
];

/** The word someone writes into Mongo, for a panel whose key is not that word. */
const PANEL_ALIASES: Record<string, string> = {
    tender: "tenderSearchProfiles",
};

/**
 * The panel keys `portalPanels` names, or `null` when it names none — then nothing is narrowed.
 *
 * Written by hand, so read tolerantly: a single string counts as a list of one, an alias maps to
 * its key, blanks and non-strings are skipped. An unknown name matches no panel and so adds
 * nothing; the always-visible panels stay regardless.
 */
export const configuredPortalPanels = (customer: any): Set<string> | null => {
    const raw = customer?.configuration?.portalPanels;
    const values: unknown[] = typeof raw === "string" ? [raw] : Array.isArray(raw) ? raw : [];
    const keys = values
        .filter((value): value is string => typeof value === "string" && value.trim() !== "")
        .map((value) => value.trim())
        .map((value) => PANEL_ALIASES[value.toLowerCase()] ?? value);
    return keys.length > 0 ? new Set(keys) : null;
};

/**
 * The sidebar items that remain under `configured`. Headings stay only where an item follows
 * before the next heading — a group whose entries were all hidden would otherwise stand empty.
 */
export const restrictToPortalPanels = <T extends { type: string; key: string }>(
    items: T[],
    configured: Set<string> | null,
): T[] => {
    if (!configured) return items;
    const kept = items.filter(
        (item) =>
            item.type === "heading" ||
            ALWAYS_VISIBLE_PANELS.includes(item.key) ||
            configured.has(item.key),
    );
    return kept.filter(
        (item, index) => item.type !== "heading" || kept[index + 1]?.type === "item",
    );
};

/** The tabs of "Bestellungen", in order. Abrechnung and Statistiken are no sidebar entries of their own. */
export const ORDERS_SECTIONS = ["orders", "billing", "statistics"] as const;
export type OrdersSection = (typeof ORDERS_SECTIONS)[number];

/**
 * The tab an old `?panel=billing` / `?panel=statistics` link means now, or `null` for any other
 * panel. Links like that exist outside the portal (the Stripe onboarding return URL falls back to
 * `?panel=billing`), so they are redirected rather than left to open nothing.
 */
export const sectionForMovedPanel = (panel: string | null | undefined): OrdersSection | null =>
    panel === "billing" || panel === "statistics" ? panel : null;
